import axios from "axios";
import {saveAs} from "file-saver";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import * as API_URL from "../../constants/ApiUrl";
import * as APIUrl from "../../constants/ApiUrl";
import * as UserQuery from "../../constants/query/User.query";
import * as Routes from "../../constants/Routes";
import {InitialEmptyProps} from "../common/empty/Empty.props";
import List from "../common/list/List";
import {InitialUserListProps, ListColumnType, ListPropsForUser} from "../common/list/List.props";
import Pagination from "../common/pagination/Pagination";
import {InitialPaginationProps, PaginationProps} from "../common/pagination/Pagination.props";
import Popup from "../common/popup/Popup";
import {AlertPopupProps} from "../common/popup/Popup.props";
import {Option} from "../common/searchForm/Option";
import SearchForm from "../common/searchForm/SearchForm";
import {UserSearchFormProps} from "./constants/SearchForm.props";
import {HEADERS} from "./constants/UserList.props";
import {SearchRequestDto} from "./dto/SearchRequest.dto";
import {UserDto} from "./dto/User.dto";
import FeatureButton from "./featureButton/FeatureButton";
import "./style.css";
import {SearchFormUtil} from "../common/searchForm/SearchFormUtil";

export default function User() {
    const [checkedUsers, setCheckedUsers] = useState<UserDto[]>([]);
    const [popup, setPopup] = useState<AlertPopupProps | null>(null);
    const [paginationProps, setPaginationProps] = useState<PaginationProps>(InitialPaginationProps);
    const [listProps, setListProps] = useState<ListPropsForUser<UserDto>>(InitialUserListProps);
    const [totalCount, setTotalCount] = useState<number>(0);

    const searchProps = UserSearchFormProps;
    const navigate = useNavigate();

    useEffect(() => {
        setListProps({
            headers: [...HEADERS, {
                type: ListColumnType.BUTTON,
                name: "",
                key: "",
                button: {
                    name: "상세 보기",
                    onClick: (seq: number): void => {
                        moveDetailPage(seq);
                    }
                }
            }],
            items: [],
            emptyProps: InitialEmptyProps,
            onCheckedChange: (users: UserDto[]) => {
                setCheckedUsers(users);
            }
        });
        setPaginationProps(prevState => ({
            ...prevState,
            onChange: (page: number) => {
                getUsers(page);
            }
        }));
        searchProps.onSearch = () => getUsers(0);
        searchProps.onUpdate = (options: Option[]) => {
            SearchFormUtil.setAllCheckboxesToTrue(options);
            searchProps.options = options;
        }

        getUsers(0);
    }, []);

    const getUsers = (page: number) => {
        const searchOption: SearchRequestDto = {
            isOnlyIndividual: searchProps.options[0].value[0],
            isOnlyBusiness: searchProps.options[0].value[1],
            isOnlyInstitution: searchProps.options[0].value[2],
            keywordType: searchProps.options[1].value.type,
            keyword: searchProps.options[1].value.text
        }

        axios.post(APIUrl.GRAPHQL, {query: UserQuery.GET_USERS(searchOption, page)}).then(
            response => {
                setListProps(prevState => ({
                    ...prevState,
                    items: response.data.data.getUsers.items
                }));
                setTotalCount(response.data.data.getUsers.totalItems);
                setPaginationProps(prevState => ({
                    ...prevState,
                    currentPage: response.data.data.getUsers.currentPage,
                    totalPage: response.data.data.getUsers.totalPage,
                }));
                setCheckedUsers([]);
            }
        ).catch(error => console.log(error));
    }

    const moveDetailPage = (seq: number) => {
        navigate(Routes.USER_DETAIL, {state: {seq: seq}});
    }

    const showDeletePopup = () => {
        let alertMessage = null;

        if (checkedUsers.length === 0) {
            setPopup({
                subject: "선택 항목 삭제",
                message1: "선택된 항목이 없습니다.",
                message2: null,
                alertMessage: null,
                buttonName: "취소",
                onConfirm: () => setPopup(null),
                useCloseButton: false,
                onClose: () => {
                }
            });

            return;
        }

        if (checkedUsers.find(checkedUser => checkedUser.licenses && checkedUser.licenses.length > 0)) {
            alertMessage = "라이선스 이용자가 포함되어 있습니다.";
        }

        setPopup({
            subject: "선택 항목 삭제",
            alertMessage: alertMessage,
            message1: "선택된 항목을 삭제하시겠습니까?",
            message2: "삭제된 항목은 복구할 수 없습니다.",
            buttonName: "삭제",
            onConfirm: () => deleteSelectedUser(),
            useCloseButton: true,
            onClose: () => setPopup(null),
        });
    }

    const deleteSelectedUser = (): void => {
        axios.post(APIUrl.DELETE_USERS, checkedUsers).then(
            response => {
                setPopup(null);
                getUsers(0);
            }
        ).catch(error => console.log(error));
    }

    const handleSaveAll = () => {
        axios.post(API_URL.EXPORT_USERS_TO_EXCEL, [], {responseType: "blob"})
            .then(response => {
                saveAs(response.data, "회원 목록.xlsx");
            });
    }

    const handleSave = () => {
        if (checkedUsers.length === 0) {
            setPopup({
                subject: "회원 목록 엑셀 저장",
                message1: "저장할 항목을 선택해 주세요.",
                message2: "",
                alertMessage: null,
                buttonName: "확인",
                onConfirm: () => setPopup(null),
                useCloseButton: false,
                onClose: () => {}
            });

            return;
        }

        axios.post(API_URL.EXPORT_USERS_TO_EXCEL, checkedUsers, {responseType: "blob"})
            .then(response => {
                saveAs(response.data, "회원 목록.xlsx");
            });
    }

    return (
        <>
            <div className="member-management">
                <main className="wrapper">
                    <div className="member-body">
                        <section className="list-search-section">
                            <div className="title">
                                사용자 관리
                            </div>
                            <SearchForm {...searchProps}/>
                        </section>
                        <div className="member-numbers">
                            <div className="member-wrapper">
                                <div className="member-all-number">
                                    전체 회원 수 {totalCount}명
                                </div>
                                <FeatureButton
                                    onDelete={showDeletePopup}
                                    onSaveAll={handleSaveAll}
                                    onSave={handleSave}
                                />
                            </div>
                            <List {...listProps}/>
                        </div>
                        <Pagination {...paginationProps}/>
                    </div>
                </main>
            </div>
            {popup && <Popup data={popup}/>}
        </>
    );
}